import { ApolloClient, NormalizedCacheObject } from '@apollo/client';

import { TsRestClient } from '@eluve/api-client-provider';
import { Logger } from '@eluve/logger-main';
import { markAppointmentAsCompleteMutation } from '@eluve/shared-gql-operations';

import { GenerateNoteEvent, TaskHandler } from '../types';

export const generateNoteHandler = ({
  client,
  apiClient,
  logger,
}: {
  client: ApolloClient<NormalizedCacheObject>;
  apiClient: TsRestClient | null;
  logger: Logger;
}): TaskHandler<GenerateNoteEvent> => {
  return async (task, context) => {
    if (!apiClient) {
      logger.error('API Client is not available for generating note', {
        appointmentId: task.appointmentId,
      });
      throw new Error('API client not available');
    }

    const appointmentId = task.appointmentId;
    const userCompletedAt = task?.userCompletedAt ?? new Date().toISOString();
    const shouldOverrideExistingSummary =
      task.shouldOverrideExistingSummary ?? false;
    const tenantId = context.tenantId;

    logger.info(
      `Attempting to generate summary note for appointment ${appointmentId}`,
      { appointmentId, tenantId, shouldOverrideExistingSummary },
    );

    // First mark appointment as complete
    const mutationResult = await client.mutate({
      mutation: markAppointmentAsCompleteMutation,
      variables: {
        id: appointmentId,
        userCompletedAt,
      },
    });

    if (mutationResult.errors) {
      logger.error('Failed to mark appointment as complete', {
        errors: mutationResult.errors,
        appointmentId: appointmentId,
      });
      throw new Error('Failed to mark appointment as complete');
    }

    // Only call summarizeAppointment if shouldOverrideExistingSummary is true
    if (shouldOverrideExistingSummary) {
      await apiClient.llm.summarizeAppointment({
        body: {},
        params: {
          tenantId,
          appointmentId,
        },
      });

      logger.info(
        'Successfully triggered note generation with override support',
        {
          appointmentId,
          tenantId,
          shouldOverrideExistingSummary,
        },
      );
    } else {
      logger.info(
        'Skipped note generation as shouldOverrideExistingSummary is false',
        {
          appointmentId,
          tenantId,
          shouldOverrideExistingSummary,
        },
      );
    }
  };
};
